import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Responsive, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const LONG_MONTH = /^[A-Za-z]+ \d{4}$/;
const SHORT_MONTH = /^\w{3}-\d{2}$/;

const monthNameMap = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function monthLabelToYearMonth(label) {
  if (!LONG_MONTH.test(label)) return [0, 0];
  const [monthName, yearStr] = label.split(" ");
  const monthIdx = MONTH_NAMES.indexOf(monthName);
  const year = parseInt(yearStr, 10);
  if (monthIdx === -1 || !Number.isFinite(year)) return [0, 0];
  return [year, monthIdx];
}

const toPrettyMonth = (hdr) => {
  if (LONG_MONTH.test(hdr)) return hdr;
  if (SHORT_MONTH.test(hdr)) {
    const [m, yy] = hdr.split("-");
    return `${monthNameMap[m] ?? m} 20${yy}`;
  }
  return null;
};

const parseMonthKeyFromSpread = (key) => {
  if (!key.startsWith("spread_")) return null;
  const parts = key.split("_");
  if (parts.length < 3) return null;
  const month = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
  const year = parts[2];
  return `${month} ${year}`;
};

const parseFloatSafe = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const CATEGORY_COLORS = [
  "#1976d2",
  "#388e3c",
  "#f57c00",
  "#d32f2f",
  "#7b1fa2",
  "#0097a7",
  "#c2185b",
  "#455a64",
  "#689f38",
  "#fbc02d",
  "#5d4037",
  "#00838f",
  "#ad1457",
  "#6a1b9a",
  "#ef6c00",
];

const formatCurrency = (value) => {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const FINANCIAL_FIELDS = [
  "Revised Budget",
  "ERP Job to Date Costs",
  "Estimated Cost at Completion",
  "Projected over Under",
];

const FIELD_ALIASES = {
  "Revised Budget": ["Revised Budget", "revised_budget", "Projected Budget"],
  "ERP Job to Date Costs": ["ERP Job to Date Costs", "erp_job_to_date_costs", "Job to Date Costs"],
  "Estimated Cost at Completion": ["Estimated Cost at Completion", "estimated_cost_at_completion"],
  "Projected over Under": ["Projected over Under", "Projected Over Under", "projected_over_under"],
};

const STORAGE_KEY = "forecastChartsLayout";
const VISIBILITY_KEY = "forecastChartsVisibility";
const LOG_SCALE_KEY = "forecastChartsLogScale";

const CHARTS_WITH_AXES = new Set([
  "monthlySpend",
  "sCurve",
  "stackedTrade",
  "budgetActuals",
  "variance",
]);

const DEFAULT_LOG_SCALES = Object.fromEntries(Array.from(CHARTS_WITH_AXES).map((id) => [id, true]));

const CHART_DEFS = [
  { id: "monthlySpend", title: "Expenses Per Month" },
  { id: "categoryDonut", title: "Expense Category Distribution" },
  { id: "sCurve", title: "Cumulative Cash Flow (S-Curve)" },
  { id: "stackedTrade", title: "Monthly Spend by Trade" },
  { id: "budgetActuals", title: "Budget vs. Actuals vs. Forecast" },
  { id: "variance", title: "Cost Variance (Over/Under Budget)" },
];

const DEFAULT_LAYOUTS = {
  lg: [
    { i: "monthlySpend", x: 0, y: 0, w: 6, h: 5, minW: 3, minH: 3 },
    { i: "categoryDonut", x: 6, y: 0, w: 6, h: 5, minW: 3, minH: 3 },
    { i: "sCurve", x: 0, y: 5, w: 6, h: 5, minW: 3, minH: 3 },
    { i: "stackedTrade", x: 6, y: 5, w: 6, h: 5, minW: 3, minH: 3 },
    { i: "budgetActuals", x: 0, y: 10, w: 6, h: 5, minW: 3, minH: 3 },
    { i: "variance", x: 6, y: 10, w: 6, h: 5, minW: 3, minH: 3 },
  ],
};

function loadSavedLayouts() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const NO_MONTHLY_DATA_MSG =
  "No monthly forecast yet. Set a curve on a row to spread its forecast across months.";

/** Latest forecast end date across rows, or null. Accepts API rows and CSV ($columns/$data). */
function getLatestEndDate(df) {
  let values = [];
  if (Array.isArray(df)) values = df.map((r) => r?.end_date ?? r?.["End Date"]);
  else if (df?.$columns && df?.$data) {
    const idx = df.$columns.findIndex((c) => c === "End Date" || c === "end_date");
    if (idx !== -1) values = df.$data.map((arr) => arr[idx]);
  }
  let latest = null;
  for (const v of values) {
    if (!v) continue;
    const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(d.getTime()) && (!latest || d > latest)) latest = d;
  }
  return latest;
}

function getNoMonthlyDataMessage(latestEnd) {
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (!latestEnd || latestEnd >= currentMonth) return NO_MONTHLY_DATA_MSG;
  const ended = latestEnd.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return `Every forecast row ends by ${ended}. Procore only returns monthly amounts from the current month onward, so there is nothing to chart. Extend row end dates to see monthly spend.`;
}

function EmptyChart({ message }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 3,
        textAlign: "center",
        color: "text.secondary",
      }}
    >
      <InsertChartOutlinedIcon sx={{ fontSize: 40, color: "grey.400" }} />
      <Typography variant="body2">{message}</Typography>
    </Box>
  );
}

const LABEL_LINE_HEIGHT = 14;
const PIE_MARGIN = { top: 50, right: 40, bottom: 100, left: 40 };
function LineChartGradientDef({ id, color = "#1976d2" }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
        <stop stopColor={color} stopOpacity={0.5} />
        <stop offset="1" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

function DonutWithLabels({ categoryLabels, categoryValues }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [hiddenIndices, setHiddenIndices] = useState(new Set());

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleHiddenItemsChange = useCallback((items) => {
    setHiddenIndices(new Set(items.map((h) => h.dataIndex)));
  }, []);

  const drawW = dims.w - PIE_MARGIN.left - PIE_MARGIN.right;
  const drawH = dims.h - PIE_MARGIN.top - PIE_MARGIN.bottom;
  const outerR = Math.max(Math.min(drawW, drawH) / 2, 10);
  const innerR = Math.round(outerR * 0.5);
  const pieCx = PIE_MARGIN.left + drawW / 2;
  const pieCy = PIE_MARGIN.top + drawH / 2;

  const pieData = useMemo(
    () =>
      categoryLabels.map((label, i) => ({
        id: i,
        value: categoryValues[i],
        label,
        color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
      })),
    [categoryLabels, categoryValues]
  );

  const visibleTotal = useMemo(() => {
    let sum = 0;
    categoryValues.forEach((v, i) => {
      if (!hiddenIndices.has(i)) sum += Math.abs(v || 0);
    });
    return sum;
  }, [categoryValues, hiddenIndices]);

  const outerLabels = useMemo(() => {
    if (dims.w === 0 || dims.h === 0 || visibleTotal === 0) return [];
    const lineLen = 18;

    let cumAngle = 0;
    const items = [];
    categoryLabels.forEach((label, i) => {
      if (hiddenIndices.has(i)) return;
      const value = categoryValues[i];
      const sliceAngle = (Math.abs(value) / visibleTotal) * 2 * Math.PI;
      const midAngle = cumAngle + sliceAngle / 2;
      cumAngle += sliceAngle;

      const text = `${label}: ${formatCurrency(value)}`;
      const anchorX = pieCx + outerR * Math.sin(midAngle);
      const anchorY = pieCy - outerR * Math.cos(midAngle);
      const elbowX = pieCx + (outerR + lineLen) * Math.sin(midAngle);
      const naturalY = pieCy - (outerR + lineLen) * Math.cos(midAngle);
      const isRight = Math.sin(midAngle) >= 0;
      items.push({
        anchorX,
        anchorY,
        elbowX,
        naturalY,
        isRight,
        resolvedY: naturalY,
        text,
        index: i,
      });
    });

    const yMin = LABEL_LINE_HEIGHT / 2 + 2;
    const yMax = dims.h - LABEL_LINE_HEIGHT / 2 - 2;

    const resolveOverlaps = (group) => {
      for (let pass = 0; pass < 8; pass++) {
        let moved = false;
        for (let i = 0; i < group.length; i++) {
          group[i].resolvedY = Math.max(yMin, Math.min(yMax, group[i].resolvedY));
        }
        for (let i = 1; i < group.length; i++) {
          const gap = group[i].resolvedY - group[i - 1].resolvedY;
          if (gap < LABEL_LINE_HEIGHT) {
            const shift = (LABEL_LINE_HEIGHT - gap) / 2 + 0.5;
            group[i - 1].resolvedY -= shift;
            group[i].resolvedY += shift;
            moved = true;
          }
        }
        if (!moved) break;
      }
      for (let i = 0; i < group.length; i++) {
        group[i].resolvedY = Math.max(yMin, Math.min(yMax, group[i].resolvedY));
      }
    };

    const leftItems = items.filter((it) => !it.isRight).sort((a, b) => a.naturalY - b.naturalY);
    const rightItems = items.filter((it) => it.isRight).sort((a, b) => a.naturalY - b.naturalY);
    resolveOverlaps(leftItems);
    resolveOverlaps(rightItems);
    return [...leftItems, ...rightItems];
  }, [dims, pieCx, pieCy, outerR, visibleTotal, hiddenIndices, categoryLabels, categoryValues]);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <PieChart
        series={[
          {
            data: pieData,
            innerRadius: innerR,
            outerRadius: outerR,
            cx: pieCx - PIE_MARGIN.left,
            cy: pieCy - PIE_MARGIN.top,
            valueFormatter: (v) => formatCurrency(v.value),
          },
        ]}
        hiddenItems={Array.from(hiddenIndices).map((idx) => ({
          type: "pie",
          seriesId: "auto-generated-id-0",
          dataIndex: idx,
        }))}
        onHiddenItemsChange={handleHiddenItemsChange}
        margin={PIE_MARGIN}
        slotProps={{
          legend: {
            direction: "horizontal",
            position: { vertical: "bottom", horizontal: "middle" },
            toggleVisibilityOnClick: true,
          },
        }}
      />
      {outerLabels.length > 0 && (
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {outerLabels.map((item, i) => {
            const endX = item.elbowX + (item.isRight ? 12 : -12);
            const textX = endX + (item.isRight ? 3 : -3);
            return (
              <g key={i}>
                <path
                  d={`M${item.anchorX},${item.anchorY} L${item.elbowX},${item.resolvedY} L${endX},${item.resolvedY}`}
                  stroke="#666"
                  fill="none"
                  strokeWidth={1}
                />
                <text
                  x={textX}
                  y={item.resolvedY}
                  textAnchor={item.isRight ? "start" : "end"}
                  dominantBaseline="middle"
                  fontSize={10}
                  fill="#333"
                >
                  {item.text}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function ChartContainer({ children }) {
  return <div style={{ position: "absolute", inset: 0 }}>{children}</div>;
}

const LogSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(theme.palette.getContrastText(theme.palette.primary.main))}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(theme.palette.getContrastText(theme.palette.primary.main))}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

function ChartWidget({ title, onHide, logScale, onToggleLog, children }) {
  return (
    <Paper
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 2,
        cursor: "default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
          cursor: "grab",
          userSelect: "none",
          minHeight: 40,
          "&:active": { cursor: "grabbing" },
        }}
        className="drag-handle"
      >
        <DragIndicatorIcon sx={{ color: "text.secondary", fontSize: 20, mr: 0.5 }} />
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        {onToggleLog && (
          <Tooltip title={logScale ? "Switch to linear scale" : "Switch to logarithmic scale"}>
            <FormControlLabel
              control={<LogSwitch checked={!!logScale} onChange={onToggleLog} />}
              label={
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    lineHeight: 1,
                    color: logScale ? "primary.main" : "text.secondary",
                    bgcolor: logScale ? "primary.50" : "transparent",
                    borderRadius: 1,
                    px: 0.75,
                    "&:hover": { bgcolor: logScale ? "primary.100" : "action.hover" },
                  }}
                >
                  LOG AXES
                </Typography>
              }
              sx={{
                mr: 0.5,
                ml: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 11,
                  fontWeight: 600,
                  color: "text.secondary",
                  lineHeight: 1,
                },
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Hide this chart">
          <IconButton size="small" onClick={onHide}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, p: 1.5, position: "relative", minHeight: 0, cursor: "default" }}>
        <ChartContainer>{children}</ChartContainer>
      </Box>
    </Paper>
  );
}

export default function ForecastCharts({ df }) {
  const chartData = useMemo(() => {
    const empty = {
      monthLabels: [],
      monthlySpend: [],
      cumulativeSpend: [],
      categoryLabels: [],
      categoryValues: [],
      stackedByCategory: {},
      budgetComparison: {},
      varianceByCategory: {},
    };
    if (!df) return empty;

    let normalized = [];

    if (Array.isArray(df)) {
      normalized = df.map((raw) => {
        const row = {};
        Object.entries(raw).forEach(([k, v]) => {
          const pretty = parseMonthKeyFromSpread(k);
          if (pretty) row[pretty] = parseFloatSafe(v?.amount ?? v);
        });

        row["Projected Cost to Complete"] =
          parseFloatSafe(raw["Projected Cost to Complete"]) ||
          parseFloatSafe(raw["Projected Cost To Complete"]) ||
          parseFloatSafe(raw["Estimated Cost at Completion"]) -
            parseFloatSafe(raw["Job to Date Costs"]);

        row["Cost Code Tier 1"] =
          raw["Cost Code Tier 1"] || raw.cost_code_level_1 || raw.root_cost_code || "Unspecified";

        for (const field of FINANCIAL_FIELDS) {
          const aliases = FIELD_ALIASES[field] || [field];
          for (const alias of aliases) {
            if (raw[alias] !== undefined) {
              row[field] = parseFloatSafe(raw[alias]);
              break;
            }
          }
          if (row[field] === undefined) row[field] = 0;
        }

        return row;
      });
    } else if (df.$columns && df.$data) {
      normalized = df.$data.map((arr) => {
        const row = {};
        df.$columns.forEach((col, i) => {
          const pretty = toPrettyMonth(col);
          if (pretty) {
            row[pretty] = parseFloatSafe(arr[i]);
          } else if (col === "Projected Cost to Complete" || col === "Projected Cost To Complete") {
            row["Projected Cost to Complete"] = parseFloatSafe(arr[i]);
          } else if (col === "Cost Code Tier 1") {
            row["Cost Code Tier 1"] = arr[i] || "Unspecified";
          }
          for (const field of FINANCIAL_FIELDS) {
            const aliases = FIELD_ALIASES[field] || [field];
            if (aliases.includes(col)) {
              row[field] = parseFloatSafe(arr[i]);
            }
          }
        });
        for (const field of FINANCIAL_FIELDS) {
          if (row[field] === undefined) row[field] = 0;
        }
        return row;
      });
    } else {
      return empty;
    }

    const monthSet = new Set();
    normalized.forEach((r) => {
      Object.keys(r).forEach((k) => {
        if (LONG_MONTH.test(k)) monthSet.add(k);
      });
    });
    const monthLabels = Array.from(monthSet).sort((a, b) => {
      const [yA, mA] = monthLabelToYearMonth(a);
      const [yB, mB] = monthLabelToYearMonth(b);
      return yA !== yB ? yA - yB : mA - mB;
    });

    const monthlySpend = monthLabels.map((m) =>
      normalized.reduce((sum, r) => sum + parseFloatSafe(r[m]), 0)
    );

    const cumulativeSpend = [];
    monthlySpend.reduce((acc, val) => {
      const cum = acc + val;
      cumulativeSpend.push(cum);
      return cum;
    }, 0);

    const categoryTotals = {};
    normalized.forEach((r) => {
      const cat = r["Cost Code Tier 1"] || "Unspecified";
      categoryTotals[cat] =
        (categoryTotals[cat] || 0) + parseFloatSafe(r["Projected Cost to Complete"]);
    });
    const categoryLabels = Object.keys(categoryTotals);
    const categoryValues = categoryLabels.map((k) => categoryTotals[k]);

    const stackedByCategory = {};
    normalized.forEach((r) => {
      const cat = r["Cost Code Tier 1"] || "Unspecified";
      if (!stackedByCategory[cat]) stackedByCategory[cat] = {};
      monthLabels.forEach((m) => {
        stackedByCategory[cat][m] = (stackedByCategory[cat][m] || 0) + parseFloatSafe(r[m]);
      });
    });

    const budgetComparison = {};
    normalized.forEach((r) => {
      const cat = r["Cost Code Tier 1"] || "Unspecified";
      if (!budgetComparison[cat]) {
        budgetComparison[cat] = { revisedBudget: 0, erpJtdCosts: 0, estimatedCostAtCompletion: 0 };
      }
      budgetComparison[cat].revisedBudget += r["Revised Budget"] || 0;
      budgetComparison[cat].erpJtdCosts += r["ERP Job to Date Costs"] || 0;
      budgetComparison[cat].estimatedCostAtCompletion += r["Estimated Cost at Completion"] || 0;
    });

    const varianceByCategory = {};
    normalized.forEach((r) => {
      const cat = r["Cost Code Tier 1"] || "Unspecified";
      varianceByCategory[cat] = (varianceByCategory[cat] || 0) + (r["Projected over Under"] || 0);
    });

    return {
      monthLabels,
      monthlySpend,
      cumulativeSpend,
      categoryLabels,
      categoryValues,
      stackedByCategory,
      budgetComparison,
      varianceByCategory,
    };
  }, [df]);

  const {
    monthLabels,
    monthlySpend,
    cumulativeSpend,
    categoryLabels,
    categoryValues,
    stackedByCategory,
    budgetComparison,
    varianceByCategory,
  } = chartData;

  const defaultVisible = Object.fromEntries(CHART_DEFS.map((c) => [c.id, true]));
  const [visibility, setVisibility] = useState(() => loadSavedVisibility() || defaultVisible);
  const [layouts, setLayouts] = useState(() => loadSavedLayouts() || DEFAULT_LAYOUTS);

  const [logScales, setLogScales] = useState(() => {
    try {
      const raw = localStorage.getItem(LOG_SCALE_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_LOG_SCALES;
    } catch {
      return DEFAULT_LOG_SCALES;
    }
  });

  const toggleLogScale = useCallback((id) => {
    setLogScales((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(LOG_SCALE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const { width: containerWidth, containerRef, mounted } = useContainerWidth();

  const toggleVisibility = useCallback((id) => {
    setVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const hideChart = useCallback((id) => {
    setVisibility((prev) => {
      const next = { ...prev, [id]: false };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleLayoutChange = useCallback((_current, allLayouts) => {
    setLayouts((prev) => {
      const merged = {};
      const allBps = new Set([...Object.keys(prev), ...Object.keys(allLayouts)]);
      for (const bp of allBps) {
        const prevItems = prev[bp] || [];
        const newItems = allLayouts[bp] || [];
        const newMap = Object.fromEntries(newItems.map((item) => [item.i, item]));
        const seen = new Set();
        const result = prevItems.map((item) => {
          seen.add(item.i);
          return newMap[item.i] || item;
        });
        for (const item of newItems) {
          if (!seen.has(item.i)) result.push(item);
        }
        merged[bp] = result;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  if (!monthLabels.length && !categoryLabels.length)
    return (
      <Box sx={{ mt: 4, mb: 6, textAlign: "center", color: "text.secondary" }}>No Data Yet</Box>
    );

  /* ---------- Log-scale helpers ---------- */
  const LOG_FLOOR = 1;
  // Log scales can't plot values <= 0, so clamp them to the floor.
  const logSafeLines = (arr, isLog) =>
    isLog ? arr.map((v) => (v > LOG_FLOOR ? v : LOG_FLOOR)) : arr;
  const hasMonthlyData = monthLabels.length > 0 && monthlySpend.some((v) => v !== 0);
  const noMonthlyDataMsg = hasMonthlyData ? "" : getNoMonthlyDataMessage(getLatestEndDate(df));

  const isPow10 = (v) => {
    if (v <= 0) return false;
    const log = Math.log10(v);
    return Math.abs(log - Math.round(log)) < 1e-6;
  };

  const logMax = (...arrays) => {
    let max = 0;
    for (const arr of arrays) {
      for (const v of arr) {
        const abs = Math.abs(v);
        if (abs > max) max = abs;
      }
    }
    return max > 1 ? Math.pow(10, Math.ceil(Math.log10(max))) : 1000;
  };

  const logAxisProps = (isLog, ...dataArrays) => {
    if (!isLog) return {};
    return { min: LOG_FLOOR, max: logMax(...dataArrays) };
  };

  const yLogFormatter = (v) => (v <= LOG_FLOOR ? "$0" : formatCurrency(v));
  const seriesFormatter = (v) => formatCurrency(v <= LOG_FLOOR ? 0 : v);
  const logTickFilter = (value) => isPow10(value);

  const buildYAxis = (isLog, label, dataArrays, logMode = "log") => {
    if (!isLog) {
      return {
        scaleType: "linear",
        label,
        valueFormatter: formatCurrency,
      };
    }

    if (logMode === "symlog") {
      const max = logMax(...dataArrays);
      return {
        scaleType: "symlog",
        label,
        valueFormatter: formatCurrency,
        min: 0,
        max,
        tickInterval: (value) => value === 0 || isPow10(value),
      };
    }

    return {
      scaleType: "log",
      label,
      valueFormatter: yLogFormatter,
      ...logAxisProps(true, ...dataArrays),
      tickInterval: logTickFilter,
    };
  };

  /* ---------- Chart configs ---------- */
  const charts = {};

  const legendBottom = {
    legend: {
      position: { vertical: "bottom", horizontal: "middle" },
      toggleVisibilityOnClick: true,
    },
  };

  charts.monthlySpend = !hasMonthlyData ? (
    <EmptyChart message={noMonthlyDataMsg} />
  ) : (
    <LineChart
      series={[
        {
          data: logSafeLines(monthlySpend, logScales.monthlySpend),
          label: "Monthly Spend",
          // An area fill is drawn down to 0, which a log axis can't render.
          area: !logScales.monthlySpend,
          curve: "monotoneX",
          valueFormatter: seriesFormatter,
        },
      ]}
      xAxis={[{ data: monthLabels, scaleType: "point", label: "Month" }]}
      yAxis={[buildYAxis(logScales.monthlySpend, "Total Spend ($)", [monthlySpend])]}
      grid={{ horizontal: true }}
      slotProps={legendBottom}
      margin={{ bottom: 5 }}
      sx={{ "& .MuiLineChart-area": { fill: "url(#monthlySpendGradient)" } }}
    >
      <LineChartGradientDef id="monthlySpendGradient" />
    </LineChart>
  );

  charts.categoryDonut = (
    <DonutWithLabels categoryLabels={categoryLabels} categoryValues={categoryValues} />
  );

  charts.sCurve = !hasMonthlyData ? (
    <EmptyChart message={noMonthlyDataMsg} />
  ) : (
    <LineChart
      series={[
        {
          data: logSafeLines(cumulativeSpend, logScales.sCurve),
          label: "Cumulative Cash Flow",
          area: !logScales.sCurve,
          color: "#1976d2",
          curve: "monotoneX",
          valueFormatter: seriesFormatter,
        },
      ]}
      xAxis={[{ data: monthLabels, scaleType: "point", label: "Month" }]}
      yAxis={[buildYAxis(logScales.sCurve, "Cumulative Spend ($)", [cumulativeSpend])]}
      grid={{ horizontal: true }}
      slotProps={legendBottom}
      margin={{ bottom: 5 }}
      sx={{ "& .MuiLineChart-area": { fill: "url(#sCurveGradient)" } }}
    >
      <LineChartGradientDef id="sCurveGradient" color="#1976d2" />
    </LineChart>
  );

  const stackedCategories = Object.keys(stackedByCategory);
  charts.stackedTrade = !hasMonthlyData ? (
    <EmptyChart message={noMonthlyDataMsg} />
  ) : (
    <BarChart
      borderRadius={4}
      series={stackedCategories.map((cat, i) => ({
        data: monthLabels.map((m) => stackedByCategory[cat][m] || 0),
        label: cat,
        stack: "total",
        color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
        valueFormatter: (v) => formatCurrency(v),
      }))}
      xAxis={[{ data: monthLabels, scaleType: "band", label: "Month" }]}
      yAxis={[buildYAxis(logScales.stackedTrade, "Total Spend ($)", [monthlySpend], "symlog")]}
      grid={{ horizontal: true }}
      slotProps={legendBottom}
      margin={{ bottom: 5 }}
    />
  );

  const budgetCategories = Object.keys(budgetComparison).filter((cat) => {
    const d = budgetComparison[cat];
    return d.revisedBudget !== 0 || d.erpJtdCosts !== 0 || d.estimatedCostAtCompletion !== 0;
  });
  charts.budgetActuals = !budgetCategories.length ? (
    <EmptyChart message="No budget or cost data to compare yet." />
  ) : (
    <BarChart
      borderRadius={4}
      series={[
        {
          data: budgetCategories.map((c) => budgetComparison[c].revisedBudget),
          label: "Revised Budget",
          color: "#1976d2",
          valueFormatter: (v) => formatCurrency(v),
        },
        {
          data: budgetCategories.map((c) => budgetComparison[c].erpJtdCosts),
          label: "Actuals (ERP JTD)",
          color: "#388e3c",
          valueFormatter: (v) => formatCurrency(v),
        },
        {
          data: budgetCategories.map((c) => budgetComparison[c].estimatedCostAtCompletion),
          label: "Estimated at Completion",
          color: "#f57c00",
          valueFormatter: (v) => formatCurrency(v),
        },
      ]}
      xAxis={[{ data: budgetCategories, scaleType: "band" }]}
      yAxis={[
        {
          ...buildYAxis(
            logScales.budgetActuals,
            "Cost ($)",
            [
              budgetCategories.map((c) => budgetComparison[c].revisedBudget),
              budgetCategories.map((c) => budgetComparison[c].erpJtdCosts),
              budgetCategories.map((c) => budgetComparison[c].estimatedCostAtCompletion),
            ],
            "symlog"
          ),
          width: 86,
        },
      ]}
      grid={{ horizontal: true }}
      slotProps={legendBottom}
      margin={{ bottom: 5 }}
    />
  );

  const varianceCategories = Object.keys(varianceByCategory).filter(
    (cat) => varianceByCategory[cat] !== 0
  );
  const varianceValues = varianceCategories.map((c) => varianceByCategory[c]);
  const isVarianceLog = !!logScales.variance;

  const getSymlogTicks = (values) => {
    const max = logMax(values);
    const ticks = [0];
    for (let p = 1; p <= max; p *= 10) {
      ticks.push(-p, p);
    }
    return ticks.sort((a, b) => a - b);
  };

  charts.variance = !varianceCategories.length ? (
    <EmptyChart message="No over/under budget variance yet." />
  ) : (
    <BarChart
      borderRadius={4}
      layout="horizontal"
      series={[
        {
          data: varianceValues,
          label: "Projected Over/Under",
          valueFormatter: (v) => formatCurrency(v),
        },
      ]}
      yAxis={[{ data: varianceCategories, scaleType: "band", width: 100 }]}
      xAxis={[
        {
          scaleType: isVarianceLog ? "symlog" : "linear",
          label: "Over / Under ($)",
          valueFormatter: (v) => formatCurrency(v),
          ...(isVarianceLog && {
            tickInterval: getSymlogTicks(varianceValues),
          }),
          colorMap: {
            type: "piecewise",
            thresholds: [0],
            colors: ["#d32f2f", "#388e3c"],
          },
        },
      ]}
      hideLegend
      grid={{ vertical: true }}
    />
  );

  const visibleCharts = CHART_DEFS.filter((c) => visibility[c.id]);
  const visibleIds = new Set(visibleCharts.map((c) => c.id));
  const visibleLayouts = {};
  for (const bp of Object.keys(layouts)) {
    const bpItems = (layouts[bp] || []).filter((item) => visibleIds.has(item.i));
    const present = new Set(bpItems.map((item) => item.i));
    const fallback = (DEFAULT_LAYOUTS[bp] || DEFAULT_LAYOUTS.lg || []).filter(
      (item) => visibleIds.has(item.i) && !present.has(item.i)
    );
    visibleLayouts[bp] = [...bpItems, ...fallback];
  }
  for (const bp of Object.keys(DEFAULT_LAYOUTS)) {
    if (!visibleLayouts[bp]) {
      visibleLayouts[bp] = (DEFAULT_LAYOUTS[bp] || []).filter((item) => visibleIds.has(item.i));
    }
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {/* Visibility toggle toolbar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          px: 1,
          alignItems: "center",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, color: "text.secondary" }}>
          Charts:
        </Typography>
        {CHART_DEFS.map((def) => (
          <Chip
            key={def.id}
            label={def.title}
            size="small"
            icon={
              visibility[def.id] ? (
                <VisibilityIcon fontSize="small" />
              ) : (
                <VisibilityOffIcon fontSize="small" />
              )
            }
            variant={visibility[def.id] ? "filled" : "outlined"}
            color={visibility[def.id] ? "primary" : "default"}
            onClick={() => toggleVisibility(def.id)}
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Box>

      <div ref={containerRef}>
        {visibleCharts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body1">
              All charts are hidden. Click a chip above to show a chart.
            </Typography>
          </Box>
        ) : (
          mounted && (
            <Responsive
              key={Array.from(visibleIds).sort().join(",")}
              width={containerWidth}
              layouts={visibleLayouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
              rowHeight={80}
              dragConfig={{ handle: ".drag-handle" }}
              onLayoutChange={handleLayoutChange}
              compactType="vertical"
              isResizable
              isDraggable
            >
              {visibleCharts.map((def) => (
                <div key={def.id}>
                  <ChartWidget
                    title={def.title}
                    onHide={() => hideChart(def.id)}
                    logScale={!!logScales[def.id]}
                    onToggleLog={
                      CHARTS_WITH_AXES.has(def.id) ? () => toggleLogScale(def.id) : undefined
                    }
                  >
                    {charts[def.id]}
                  </ChartWidget>
                </div>
              ))}
            </Responsive>
          )
        )}
      </div>
    </Box>
  );
}
